import type { AzDanceBookingSummaryParticipantDto } from '@/api/api-client/service';
import type { RegisteredParticipant } from '@/state/stores/registrationStore';

/** Server participant id (ULID). */
const SERVER_PARTICIPANT_ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

/**
 * Championship pair on the booking: persisted co-participant id (not self).
 * Draft numeric `copartnerId` from the API is ignored for “championship” display.
 */
function summaryHasPersistedCopartnerUlid(
    sp: Pick<AzDanceBookingSummaryParticipantDto, 'id' | 'copartnerId'>,
): boolean {
    const cop = sp.copartnerId?.trim();
    return (
        !!cop &&
        cop !== sp.id &&
        SERVER_PARTICIPANT_ULID_RE.test(cop)
    );
}

function summaryRegistrationMode(
    sp: AzDanceBookingSummaryParticipantDto,
): 'default' | 'championship' | undefined {
    const rm = (sp as { registrationMode?: string }).registrationMode;
    if (rm === 'championship' || rm === 'default') return rm;
    return undefined;
}

/** From GET summary / participants — set store flags for championship vs guest. */
export function deriveChampionshipFlagsFromSummaryParticipant(
    sp: AzDanceBookingSummaryParticipantDto,
): { isChampionshipParticipant: boolean; isSolo: boolean } {
    const mode = summaryRegistrationMode(sp);
    if (mode === 'championship') {
        const hasUlidPartner = summaryHasPersistedCopartnerUlid(sp);
        return { isChampionshipParticipant: true, isSolo: !hasUlidPartner };
    }
    if (mode === 'default') {
        return { isChampionshipParticipant: false, isSolo: false };
    }
    const hasUlidPartner = summaryHasPersistedCopartnerUlid(sp);
    const isChampionshipParticipant = hasUlidPartner;
    const isSolo = isChampionshipParticipant && !hasUlidPartner;
    return { isChampionshipParticipant, isSolo };
}

/**
 * UI: guest vs championship badge (covers API-mapped rows and older persisted store).
 */
export function registeredParticipantDisplaysAsChampionship(
    p: RegisteredParticipant,
): boolean {
    if (p.registrationMode === 'championship') return true;
    if (p.registrationMode === 'default') return false;
    if (p.isChampionshipParticipant) return true;
    const link = p.partnerId?.trim();
    if (
        link &&
        link !== p.id &&
        SERVER_PARTICIPANT_ULID_RE.test(link)
    ) {
        return true;
    }
    return false;
}
